// Centralized, easily-editable translations. Add/adjust strings here.
// Each key exists in both `nl` and `en`.

export const translations = {
  nl: {
    nav: {
      problem: "Het probleem",
      idea: "Het idee",
      proof: "Bewijs",
      vision: "Visie",
      contact: "Contact",
      investors: "Investeerders / Klanten",
      login: "Inloggen",
      getStarted: "Aan de slag",
      dashboard: "Dashboard",
      logout: "Uitloggen",
    },
    hero: {
      badge: "Eén fundament. Onbeperkte mogelijkheden.",
      title1: "Bezit jouw identiteit.",
      title2: "Verbind jouw wereld.",
      subtitle:
        "Eén identiteit voor je digitale leven. Je data blijft verbonden met jou. Jij bepaalt wat je deelt, met wie en waarom.",
      ctaPrimary: "Maak je 1iD aan",
      ctaSecondary: "Bekijk hoe het werkt",
    },
    problem: {
      overline: "Het probleem",
      todayLabel: "Vandaag",
      todayHeadline: "20 apps. 20 accounts. 20 kopieën van jou.",
      todayBody:
        "Elke app heeft zijn eigen login, profiel, data en voorkeuren. Je identiteit ligt versnipperd over tientallen plekken.",
      oneLabel: "Met 1iD",
      oneHeadline: "Eén jij. Eén identiteit.",
      oneBody:
        "De apps veranderen. Jij blijft dezelfde. Eén identiteit die met je meebeweegt — overal.",
    },
    idea: {
      overline: "Het idee",
      headline: "Stop met mensen rond apps bouwen. Bouw apps rond mensen.",
      tradLabel: "Traditioneel",
      tradFlow: ["App", "Gebruiker", "Data"],
      idLabel: "1iD",
      idFlow: ["Identiteit", "Data", "Capabilities"],
      killer: "De applicatie is niet langer het middelpunt. Jij bent het.",
    },
    proof: {
      overline: "Show, don't explain",
      headline: "Geen concept. Het werkt al.",
      brand: "Connected Words — powered by 1iD",
      body:
        "Een fysiek T-shirt heeft een unieke digitale identiteit. De eigenaar bepaalt zijn QR-profiel. Het profiel kan veranderen — het product behoudt zijn identiteit.",
      passportLabel: "Digitaal productpaspoort",
      features: [
        "Unieke QR-code",
        "Eigendomsverificatie",
        "Producthistorie",
        "Gepersonaliseerd profiel",
      ],
    },
    vision: {
      overline: "De visie",
      headline: "Begin met identiteit. Groei zonder het fundament opnieuw te bouwen.",
      aiPill: "Betrouwbare identiteit → betrouwbare data → betere AI",
      items: [
        { title: "Mensen", desc: "Personal Apps" },
        { title: "Producten", desc: "Blijvende digitale identiteiten" },
        { title: "Bedrijven", desc: "Company Apps" },
        { title: "AI", desc: "Betrouwbare context voor AI-agents" },
      ],
      footer: "Eén identiteit. Eén fundament. Onbeperkte mogelijkheden.",
      explore: "Ontdek de volledige visie",
    },
    cta: {
      peopleLabel: "Voor mensen",
      peopleHeadline: "Bezit je identiteit. Start je Personal ERP.",
      peopleCta: "Maak je 1iD aan",
      investorsLabel: "Voor investeerders & partners",
      investorsHeadline: "Eén fundament. Meerdere markten. Samengestelde waarde.",
      investorsCta: "Ontdek de investeringskans",
      privacy: "Jouw identiteit. Jouw data. Jouw controle.",
    },
    social: {
      trouble: "Video niet zichtbaar?",
      watch: "Bekijk op YouTube",
    },
    footer: {
      tagline: "Bezit je identiteit. Verbind je wereld.",
      rights: "Alle rechten voorbehouden.",
      product: "Product",
      company: "Bedrijf",
      legal: "Juridisch",
    },
    auth: {
      loginTitle: "Welkom terug",
      loginSubtitle: "Log in op je 1iD om verder te gaan.",
      registerTitle: "Maak je 1iD aan",
      registerSubtitle: "Bezit je identiteit. Begin in seconden.",
      name: "Naam",
      email: "E-mailadres",
      password: "Wachtwoord",
      loginBtn: "Inloggen",
      registerBtn: "Account aanmaken",
      noAccount: "Nog geen account?",
      haveAccount: "Heb je al een account?",
      signUp: "Registreren",
      signIn: "Inloggen",
      forgot: "Wachtwoord vergeten?",
      identitySection: "Identiteit",
      addressSection: "Woonadres",
      accountSection: "Account",
      firstName: "Voornaam",
      lastName: "Achternaam",
      phone: "Telefoon (optioneel)",
      dob: "Geboortedatum (optioneel)",
      street: "Straat",
      number: "Nr.",
      postalCode: "Postcode",
      city: "Gemeente",
      country: "Land",
      message: "Boodschap (optioneel)",
    },
    dashboard: {
      welcome: "Welkom",
      personalApps: "Personal Apps",
      companyApps: "Company Apps",
      personalSubtitle: "Alles over jou. Onder jouw controle.",
      companySubtitle: "Alles wat je bedrijf nodig heeft.",
      comingSoon: "Binnenkort beschikbaar",
      demoTitle: "Let op: dit is nog niet de échte 1iD",
      demoText: "Dit is een voorproefje van het platform. De échte 1iD gaat binnenkort live!",
      adminTitle: "Geregistreerde gebruikers",
      adminSubtitle: "Overzicht van early-bird registraties",
      adminExport: "Download CSV",
      adminExporting: "Bezig...",
      adminTotal: "Totaal",
      adminError: "Kon gebruikers niet laden.",
    },
    preorder: {
      badge: "1st EDITION",
      title: "Blijf op de hoogte van de pre-orderdatum.",
      subtitle: "Wees als eerste op de hoogte wanneer de pre-orders openen.",
      intro: "Laat je voornaam en e-mail achter en we informeren je zodra de Connected Words pre-order opent.",
      firstName: "Voornaam",
      email: "E-mailadres",
      consent: "Ik wil geïnformeerd worden over de Connected Words pre-order en launch.",
      privacy: "Op basis van de 1iD-principes worden je gegevens alleen gebruikt om je te informeren over de Connected Words pre-order en launch.",
      cta: "Houd me op de hoogte",
      sending: "Versturen...",
      success: "Bedankt! We houden je op de hoogte van de pre-order. 🎉",
      error: "Er ging iets mis. Probeer het later opnieuw.",
      openBtn: "Houd me op de hoogte",
    },
    contact: {
      overline: "Contact",
      title: "Stel je vraag",
      subtitle: "Kies je onderwerp en stuur ons een bericht. We reageren zo snel mogelijk.",
      topicLabel: "Onderwerp",
      topics: [
        { value: "App / product", label: "Een App / product" },
        { value: "Q&A", label: "Vraag & Antwoord (Q&A)" },
        { value: "Investeren in 1iD", label: "Investeren in 1iD" },
        { value: "Partnership / samenwerking", label: "Partnership / samenwerking" },
        { value: "Anders", label: "Anders" },
      ],
      name: "Naam",
      email: "E-mailadres",
      message: "Bericht",
      send: "Verstuur bericht",
      sending: "Versturen...",
      success: "Bedankt! Je bericht is verzonden. Je ontvangt zelf een kopie (CC).",
      stored: "Bedankt! Je bericht is ontvangen. We nemen snel contact op.",
      error: "Er ging iets mis. Probeer het later opnieuw.",
      ccNote: "Je ontvangt zelf een kopie (CC) van dit bericht op je e-mailadres.",
      humanLabel: "Even controleren dat je een mens bent",
      humanQuestion: "Hoeveel is",
      humanPlaceholder: "Antwoord",
      humanError: "Beantwoord de controlevraag juist om te verzenden.",
    },
  },

  en: {
    nav: {
      problem: "The problem",
      idea: "The idea",
      proof: "Proof",
      vision: "Vision",
      contact: "Contact",
      investors: "Investors / Customers",
      login: "Log in",
      getStarted: "Get started",
      dashboard: "Dashboard",
      logout: "Log out",
    },
    hero: {
      badge: "One Foundation. Unlimited Possibilities.",
      title1: "Own your identity.",
      title2: "Connect your world.",
      subtitle:
        "One identity for your digital life. Your data stays connected to you. You decide what to share, with whom and why.",
      ctaPrimary: "Create your 1iD",
      ctaSecondary: "See how it works",
    },
    problem: {
      overline: "The problem",
      todayLabel: "Today",
      todayHeadline: "20 apps. 20 accounts. 20 copies of you.",
      todayBody:
        "Every app has its own login, profile, data and preferences. Your identity is scattered across dozens of places.",
      oneLabel: "With 1iD",
      oneHeadline: "One you. One identity.",
      oneBody:
        "The apps change. You stay the same. One identity that moves with you — everywhere.",
    },
    idea: {
      overline: "The idea",
      headline: "Stop building people around apps. Build apps around people.",
      tradLabel: "Traditional",
      tradFlow: ["App", "User", "Data"],
      idLabel: "1iD",
      idFlow: ["Identity", "Data", "Capabilities"],
      killer: "The application is no longer the centre. You are.",
    },
    proof: {
      overline: "Show, don't explain",
      headline: "Not a concept. Already working.",
      brand: "Connected Words — powered by 1iD",
      body:
        "A physical T-shirt has a unique digital identity. The owner controls its QR Profile. The profile can change — the product keeps its identity.",
      passportLabel: "Digital product passport",
      features: [
        "Unique QR code",
        "Ownership verification",
        "Product history",
        "Personalized profile",
      ],
    },
    vision: {
      overline: "The vision",
      headline: "Start with identity. Expand without rebuilding the foundation.",
      aiPill: "Trusted identity → trusted data → better AI",
      items: [
        { title: "People", desc: "Personal Apps" },
        { title: "Products", desc: "Persistent digital identities" },
        { title: "Companies", desc: "Company Apps" },
        { title: "AI", desc: "Trusted context for AI Agents" },
      ],
      footer: "One Identity. One Foundation. Unlimited Possibilities.",
      explore: "Explore the full vision",
    },
    cta: {
      peopleLabel: "For people",
      peopleHeadline: "Own your identity. Start your Personal ERP.",
      peopleCta: "Create your 1iD",
      investorsLabel: "For investors & partners",
      investorsHeadline: "One foundation. Multiple markets. Compounding value.",
      investorsCta: "Discover the investment opportunity",
      privacy: "Your identity. Your data. Your control.",
    },
    social: {
      trouble: "Video not showing?",
      watch: "Watch on YouTube",
    },
    footer: {
      tagline: "Own your identity. Connect your world.",
      rights: "All rights reserved.",
      product: "Product",
      company: "Company",
      legal: "Legal",
    },
    auth: {
      loginTitle: "Welcome back",
      loginSubtitle: "Log in to your 1iD to continue.",
      registerTitle: "Create your 1iD",
      registerSubtitle: "Own your identity. Get started in seconds.",
      name: "Name",
      email: "Email address",
      password: "Password",
      loginBtn: "Log in",
      registerBtn: "Create account",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      signUp: "Sign up",
      signIn: "Sign in",
      forgot: "Forgot password?",
      identitySection: "Identity",
      addressSection: "Home address",
      accountSection: "Account",
      firstName: "First name",
      lastName: "Last name",
      phone: "Phone (optional)",
      dob: "Date of birth (optional)",
      street: "Street",
      number: "No.",
      postalCode: "Postal code",
      city: "City",
      country: "Country",
      message: "Message (optional)",
    },
    dashboard: {
      welcome: "Welcome",
      personalApps: "Personal Apps",
      companyApps: "Company Apps",
      personalSubtitle: "Everything about you. Under your control.",
      companySubtitle: "Everything your business needs.",
      comingSoon: "Coming soon",
      demoTitle: "Note: this is not the real 1iD yet",
      demoText: "This is a preview of the platform. The real 1iD goes live soon!",
      adminTitle: "Registered users",
      adminSubtitle: "Overview of early-bird registrations",
      adminExport: "Download CSV",
      adminExporting: "Working...",
      adminTotal: "Total",
      adminError: "Could not load users.",
    },
    preorder: {
      badge: "1st EDITION",
      title: "Stay informed about the pre-order date.",
      subtitle: "Be first to hear when pre-orders open.",
      intro: "Leave your first name and email and we'll inform you when the Connected Words pre-order opens.",
      firstName: "First name",
      email: "Email address",
      consent: "I want to be informed about the Connected Words pre-order and launch.",
      privacy: "Based on 1iD principles, your details will only be used for Connected Words pre-order and launch communication.",
      cta: "Keep me informed",
      sending: "Sending...",
      success: "Thank you! We'll keep you informed about the pre-order. 🎉",
      error: "Something went wrong. Please try again later.",
      openBtn: "Keep me informed",
    },
    contact: {
      overline: "Contact",
      title: "Ask your question",
      subtitle: "Choose your topic and send us a message. We'll respond as soon as possible.",
      topicLabel: "Topic",
      topics: [
        { value: "App / product", label: "An App / product" },
        { value: "Q&A", label: "Questions & Answers (Q&A)" },
        { value: "Investeren in 1iD", label: "Investing in 1iD" },
        { value: "Partnership / samenwerking", label: "Partnership / collaboration" },
        { value: "Anders", label: "Other" },
      ],
      name: "Name",
      email: "Email address",
      message: "Message",
      send: "Send message",
      sending: "Sending...",
      success: "Thank you! Your message has been sent. You'll receive a copy (CC).",
      stored: "Thank you! Your message has been received. We'll get back to you soon.",
      error: "Something went wrong. Please try again later.",
      ccNote: "You'll receive a copy (CC) of this message at your email address.",
      humanLabel: "Quick check that you're human",
      humanQuestion: "What is",
      humanPlaceholder: "Answer",
      humanError: "Please answer the verification question correctly to send.",
    },
  },
};
